import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { useZ3KDashboardDevices } from 'console/shared/hooks/device/z3k';
import { memo, useCallback, useMemo, useState } from 'react';
import { MaterialBoolControl } from 'console/shared/components/fields';
import { useDashboardSettings } from '../provider/types';
import { SearchField } from 'console/shared/components/fields/search';
import { DeviceStateIndicator } from 'console/shared/components/indicators/device-state';
import { displayDeviceName } from 'console/shared/contexts/device';
import { DefaultTabKey } from 'console/shared/content/tab/types';
import { preventDefault } from 'shared/hooks/utils';
import { useNavigate } from 'react-router';
import { DashboardSettingsRoutes } from '../types';
import { ChevronRight } from '@mui/icons-material';
import { isClimatic, isLegacyDevice, isVehicleDevice, } from 'console/shared/contexts/device/config/utils';
export const getDefaultDashboardDevice = (id, device_type) => ({
    id,
    tab: isVehicleDevice(device_type)
        ? DefaultTabKey.vehicle_tab
        : DefaultTabKey.heat_tab,
    items: [],
});
export const DashboardDevicesSettings = memo(function DashboardDevicesSettings() {
    const { dashboard_config, setDashboardConfig } = useDashboardSettings();
    const devices_ids = useMemo(() => dashboard_config.devices?.map(({ id }) => id) ?? [], [dashboard_config.devices]);
    const devices = useZ3KDashboardDevices();
    const [search, setSearch] = useState('');
    const filtered_devices = useMemo(() => devices.filter((device) => displayDeviceName(device).includes(search)), [search, devices]);
    const onClearSearch = useCallback(() => setSearch(''), []);
    const all_devices = useMemo(() => filtered_devices.map(({ id, device_type }) => getDefaultDashboardDevice(id, device_type)), [filtered_devices]);
    const onDeviceSelect = useCallback((device_id, device_type) => (checked) => {
        if (checked) {
            return setDashboardConfig((config) => ({
                ...config,
                devices: [
                    ...(config.devices ?? []),
                    getDefaultDashboardDevice(device_id, device_type),
                ],
            }));
        }
        setDashboardConfig((config) => ({
            ...config,
            devices: config.devices?.filter(({ id }) => device_id !== id),
        }));
    }, []);
    const navigate = useNavigate();
    const onDeviceClick = useCallback((id, name) => (event) => {
        preventDefault(event);
        navigate(DashboardSettingsRoutes.device, { state: { id, title: name } });
    }, []);
    const devices_selection = useMemo(() => filtered_devices.map((device) => {
        const { id, iconState, device_type } = device;
        const is_legacy_device = isLegacyDevice(device_type);
        const is_climatic = isClimatic(device_type);
        const name = displayDeviceName(device);
        return (_jsx(MaterialBoolControl, { value: devices_ids.includes(id), onChange: onDeviceSelect(id, device_type), sx: {
                '& .MuiFormControlLabel-label ': {
                    width: '100%',
                },
            }, custom_label: _jsx(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", onClick: is_legacy_device || is_climatic
                    ? undefined
                    : onDeviceClick(id, name), color: "text.primary", children: _jsx(Button, { variant: "text", color: "inherit", fullWidth: true, startIcon: _jsx(DeviceStateIndicator, { state: iconState }), endIcon: is_legacy_device ||
                        is_climatic ? undefined : (_jsx(ChevronRight, {})), children: name }) }) }, id));
    }), [filtered_devices, dashboard_config.devices]);
    const onAllDevicesSelectClick = useCallback((checked) => {
        if (checked) {
            return setDashboardConfig((config) => ({
                ...config,
                devices: all_devices,
            }));
        }
        setDashboardConfig((config) => ({
            ...config,
            devices: [],
        }));
    }, [all_devices]);
    const all_devices_checked = useMemo(() => all_devices.length > 0 &&
        all_devices.every(({ id }) => devices_ids.includes(id)), [all_devices, devices_ids]);
    const some_device_checked = useMemo(() => all_devices.some(({ id }) => devices_ids.includes(id)), [all_devices, devices_ids]);
    const count = useMemo(() => devices_ids.length, [devices_ids]);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 1, width: "100%", height: "100%", children: [count > 30 && (_jsx(Alert, { severity: "warning", children: _jsx(AlertTitle, { children: "\u0422\u0430\u043A\u043E\u0435 \u043A\u043E\u043B\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432 \u0442\u0440\u0435\u0431\u0443\u0435\u0442 \u0432\u044B\u0441\u043E\u043A\u0443\u044E \u043F\u0440\u043E\u0438\u0437\u0432\u043E\u0434\u0438\u0442\u0435\u043B\u044C\u043D\u043E\u0441\u0442\u044C \u043E\u0442 \u0412\u0430\u0448\u0435\u0433\u043E \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430!" }) })), count > 60 && (_jsx(Alert, { severity: "error", children: _jsx(AlertTitle, { children: "\u0420\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u0443\u0435\u043C \u0443\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u044C \u043A\u043E\u043B\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u044B\u0445 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432." }) })), _jsx(SearchField, { value: search, onChange: setSearch, onClear: onClearSearch, size: "small", sx: {
                    padding: 1,
                    borderRadius: 30,
                } }), _jsx(MaterialBoolControl, { value: all_devices_checked, indeterminate: some_device_checked && !all_devices_checked, onChange: onAllDevicesSelectClick, label: `Все устройства (${count})` }), _jsx(Box, { display: "flex", flexDirection: "column", overflow: "auto", children: _jsx(Box, { display: "grid", ml: 1, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", children: devices_selection }) })] }));
});
